/**
 * The drag-and-drop / auto-load handler wiring, factored out of main.ts so it
 * can be exercised headlessly (test/drop-handler.test.mjs) without standing up
 * the whole boot sequence (workers, OffscreenCanvas, WebGPU, SharedArrayBuffer).
 *
 * `decideLoad` (formats.ts) is the *decision* and is tested on its own; this is
 * the *wiring* around it: read the file's bytes, run the decision, and either
 *   - hand the loadable image to the CPU worker as a `loadGame` message with the
 *     buffer in the transfer list (zero-copy ownership move), or
 *   - drop it and surface the friendly reject message (encrypted containers are
 *     pointed at docs/DUMP.md, never decrypted — §1.6).
 *
 * Dependencies are injected (a `post` callback and a `log` sink) so the test can
 * drive the EXACT routing code main.ts ships — no reimplementation, no drift.
 */
import type { LogLevel, MainToCPUMessage } from "@bindings/protocol";
import { decideLoad } from "./formats";

/** Minimal shape of the dropped file we depend on (a browser File satisfies it). */
export interface DroppableFile {
  readonly name: string;
  readonly size: number;
  arrayBuffer(): Promise<ArrayBuffer>;
}

/** Posts a main→CPU message, optionally transferring buffers (zero-copy). */
export type PostToCPU = (msg: MainToCPUMessage, transfer?: Transferable[]) => void;

/** Log sink (level + message), matching log.ts's appendLogLine signature. */
export type LogSink = (level: LogLevel, message: string) => void;

/**
 * Read, sniff, and route a single dropped/fetched file. Resolves once the file
 * has been routed (posted on "load", or logged-and-dropped on "reject"); never
 * rejects — read failures are surfaced through the log sink.
 *
 * @returns true if the file was handed to the CPU worker, false otherwise.
 */
export async function handleDroppedFile(
  file: DroppableFile,
  post: PostToCPU,
  log: LogSink,
): Promise<boolean> {
  log("info", `file dropped: ${file.name} (${file.size} bytes)`);
  let buf: ArrayBuffer;
  try {
    buf = await file.arrayBuffer();
  } catch (err) {
    log("error", `read failed: ${(err as Error).message}`);
    return false;
  }

  const decision = decideLoad(new Uint8Array(buf), file.name);
  if (decision.action === "reject") {
    log("error", decision.message);
    return false;
  }

  log("info", `${file.name}: ${decision.reason} — handing to CPU worker`);
  const msg: MainToCPUMessage = { type: "loadGame", data: buf, name: file.name };
  post(msg, [buf]);
  return true;
}
